import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { WorkspacePresenceData, WorkspacePresencePayload } from '@/types/presence';

interface ConnectedAgent {
  id: string;
  name: string;
  avatar_url: string | null;
  online_at: string;
  dnd_enabled?: boolean;
  dnd_until?: string | null;
}

interface CurrentUser {
  id: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  dndEnabled?: boolean;
  dndUntil?: string | null;
}

interface UseWorkspacePresenceProps {
  workspaceId: string | undefined;
  currentUser: CurrentUser | null;
}

type Channel = ReturnType<typeof supabase.channel>;

interface PresenceEntry {
  channel: Channel;
  agents: ConnectedAgent[];
  listeners: Set<(agents: ConnectedAgent[]) => void>;
  refCount: number;
  currentPresence: WorkspacePresencePayload | null;
  subscribed: boolean;
}

const registry = new Map<string, PresenceEntry>();
const EMPTY_AGENTS: ConnectedAgent[] = [];

const buildPresence = (user: CurrentUser): WorkspacePresencePayload => {
  const name = [user.firstName, user.lastName].filter(Boolean).join(' ') || 'Agent';
  return {
    id: user.id,
    name,
    avatar_url: user.avatarUrl,
    online_at: new Date().toISOString(),
    dnd_enabled: user.dndEnabled || false,
    dnd_until: user.dndUntil || null,
  };
};

const acquire = (workspaceId: string, user: CurrentUser): PresenceEntry => {
  const existing = registry.get(workspaceId);
  if (existing) {
    existing.refCount += 1;
    existing.currentPresence = buildPresence(user);
    if (existing.subscribed) {
      existing.channel.track(existing.currentPresence);
    }
    return existing;
  }

  const channelName = `workspace-presence:${workspaceId}`;
  const channel = supabase.channel(channelName);
  const entry: PresenceEntry = {
    channel,
    agents: EMPTY_AGENTS,
    listeners: new Set(),
    refCount: 1,
    currentPresence: buildPresence(user),
    subscribed: false,
  };
  registry.set(workspaceId, entry);

  channel
    .on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      const agents: ConnectedAgent[] = [];
      Object.values(state).forEach((presences) => {
        (presences as WorkspacePresenceData[]).forEach((presence) => {
          agents.push({
            id: presence.id,
            name: presence.name,
            avatar_url: presence.avatar_url,
            online_at: presence.online_at,
            dnd_enabled: presence.dnd_enabled || false,
            dnd_until: presence.dnd_until || null,
          });
        });
      });

      const unique = agents.filter(
        (agent, index, self) => index === self.findIndex((a) => a.id === agent.id),
      );

      entry.agents = unique;
      entry.listeners.forEach((listener) => listener(unique));
    })
    .subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        entry.subscribed = true;
        if (entry.currentPresence) {
          await channel.track(entry.currentPresence);
        }
      }
    });

  return entry;
};

const release = (workspaceId: string) => {
  const entry = registry.get(workspaceId);
  if (!entry) return;
  entry.refCount -= 1;
  if (entry.refCount <= 0) {
    supabase.removeChannel(entry.channel);
    registry.delete(workspaceId);
  }
};

export const useWorkspacePresence = ({ workspaceId, currentUser }: UseWorkspacePresenceProps) => {
  const [connectedAgents, setConnectedAgents] = useState<ConnectedAgent[]>(EMPTY_AGENTS);

  useEffect(() => {
    if (!workspaceId || !currentUser) {
      setConnectedAgents(EMPTY_AGENTS);
      return;
    }

    const entry = acquire(workspaceId, currentUser);
    setConnectedAgents(entry.agents);

    const listener = (agents: ConnectedAgent[]) => setConnectedAgents(agents);
    entry.listeners.add(listener);

    return () => {
      entry.listeners.delete(listener);
      release(workspaceId);
    };
  }, [workspaceId, currentUser]);

  const updatePresence = async () => {
    if (!workspaceId || !currentUser) return;
    const current = registry.get(workspaceId);
    if (!current || !current.subscribed) return;
    current.currentPresence = buildPresence(currentUser);
    await current.channel.track(current.currentPresence);
  };

  return {
    connectedAgents,
    totalCount: connectedAgents.length,
    updatePresence,
  };
};

// Helper to check if DND is currently active (not expired)
export const isDndActive = (dndEnabled?: boolean, dndUntil?: string | null): boolean => {
  if (!dndEnabled) return false;
  if (!dndUntil) return dndEnabled;
  return new Date(dndUntil) > new Date();
};
